import { ProjectListTabs } from '@cosuno/routes';
import { queryStringToObject } from '@cosuno/utils';
import { useContext } from 'react';
import { useLocation, useMatch } from 'react-router-dom';

import { AUTH_STATUS } from '../../constants';
import { AuthRouteContext } from '../../contexts/authRouteContext';
import useAuthContext from '../useAuthContext';
import useUserAuthState from '../useUserAuthState';
import { useUserRoutes } from '../useUserRoutes';
import { urlToPathname } from './utils';

const useUserBaseUrl = (): string => {
  const { authState } = useAuthContext();

  const { isUserAnonymous, isUserPrincipal, isUserAgent } = useUserAuthState();

  const isActiveCompanyOnboarding =
    (authState.status === AUTH_STATUS.authenticated && authState.user.isActiveCompanyOnboarding) ??
    false;
  const isNavatticEnabled =
    authState.status === AUTH_STATUS.authenticated ? authState.user.isNavatticEnabled : false;

  const routes = useUserRoutes();
  const location = useLocation();
  const isLoginPage = useMatch(routes.login()) !== null;
  const { canRedirectToUrl } = useContext(AuthRouteContext);

  const { redirect } = queryStringToObject<{ redirect?: string }>(location.search);
  // remove the host from url to ensure no malicious redirects affect the app
  let redirectQuery = urlToPathname(decodeURIComponent(redirect ?? ''));
  if (
    !isUserAnonymous &&
    !canRedirectToUrl({ redirect: redirectQuery, isUserAnonymous, isUserPrincipal, isUserAgent })
  ) {
    redirectQuery = '';
  }

  if (redirectQuery) {
    return redirectQuery;
  }

  if (isUserPrincipal && isActiveCompanyOnboarding) {
    return isNavatticEnabled ? routes.projects(ProjectListTabs.open) : routes.companyOnboarding();
  }

  if (isUserAgent && isActiveCompanyOnboarding) {
    return routes.agentCompanyOnboarding();
  }

  if (isUserAnonymous && !isLoginPage) return routes.login();

  if (isUserAgent) return routes.agentBidRequests();

  if (isUserPrincipal) return routes.projects(ProjectListTabs.open);

  return routes.root();
};

export default useUserBaseUrl;
